import React, { useEffect, useState } from "react";
import styles from "../../_css/AccountSettings.module.css";
import { ArrowUpFromLine, Trash } from "lucide-react";
import { CustomButton, CustomButtonLight } from "../UI/CustomButtons";
import {
  deleteProfileImage,
  getUserDetails,
  updateUserDetails,
  updateUserImage,
} from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper";

const DEFAULT_IMAGE = `${process.env.PUBLIC_URL}/img/profile-image-default.jpg`;
const DetailsForm = ({ staticData }) => {
  const { fullName, phoneNumber, designation, companyName, fullNamePlaceholder, phoneNumberPlaceholder, designationPlaceholder, companyNamePlaceholder, fullNameError, phoneNumberError, designationError, companyNameError, changeProfileText, fileSizeError, cancelText, saveChangesText, imageSuccess, userDetailsSavedSuccessfully } = staticData;

  const [profileImage, setProfileImage] = useState(DEFAULT_IMAGE);
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState({
    full_name: "",
    phone_number: "",
    designation: "",
    company_name: "",
  });
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();

  useEffect(() => {
    handleGetUserDetails();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Check file size (3MB = 3 * 1024 * 1024 bytes)
      const maxSize = 3 * 1024 * 1024; // 3MB in bytes
      
      if (file.size > maxSize) {
        notifyError(fileSizeError);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      handleUploadProfileImage(file);
      setLoading(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
        setLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadProfileImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    dispatch(startLoader());
    try {
      const response = await updateUserImage(formData);
      if (response.status === 200) {
        notifySuccess(imageSuccess || response.data.message);

        //call handleGetUserDetails to update the profile image in the session storage
        handleGetUserDetails();
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(error?.response?.data?.message);
      console.error("Error in sending message:", error);
    }
    dispatch(stopLoader());
  };

  const handleDeleteProfileImage = async (e) => {
    e.preventDefault();
    dispatch(startLoader());
    try {
      const response = await deleteProfileImage();
      if (response.status === 200) {
        setProfileImage(DEFAULT_IMAGE);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(error?.response?.data?.message);
      console.error("Error in sending message:", error);
    }
    dispatch(stopLoader());
  };

  const handleUpdateUserDetails = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const validationErrors = {};

    // Validate each field
    // if (userDetails.full_name) {
    //   const nameError = validateFullName(userDetails.full_name);
    //   if (nameError) validationErrors.full_name = nameError;
    // } else {
    //   validationErrors.full_name = fullNameError;
    // }

    if (userDetails.phone_number) {
      const phoneError = validatePhoneNumber(userDetails.phone_number);
      if (phoneError) validationErrors.phone_number = phoneError;
    } else {
      validationErrors.phone_number = phoneNumberError;
    }

    if (userDetails.designation) {
      const designationError = validateDesignation(userDetails.designation);
      if (designationError) validationErrors.designation = designationError;
    } else {
      validationErrors.designation = designationError;
    }

    if (userDetails.company_name) {
      const companyError = validateCompanyName(userDetails.company_name);
      if (companyError) validationErrors.company_name = companyError;
    } else {
      validationErrors.company_name = companyNameError;
    }

    // Check if there are any validation errors
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear any existing errors
    setErrors({});

    dispatch(startLoader());
    try {
      const response = await updateUserDetails(userDetails);
      if (response.status === 200) {
        notifySuccess(userDetailsSavedSuccessfully || response.data.message);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(error?.response?.data?.message);
      console.error("Error in sending message:", error);
    }
    dispatch(stopLoader());
  };

  const handleGetUserDetails = async () => {
    dispatch(startLoader());
    try {
      const response = await getUserDetails();
      if (response.status === 200) {
        const userDetails = response.data.userDetails;
        console.log(userDetails);
        setUserDetails(userDetails);
        
        if(userDetails.image_url){
          setProfileImage(userDetails.image_url);
          //update profileImage in session storage in sal_userInfo
          const userInfo = JSON.parse(sessionStorage.getItem("sal_userInfo"));
          userInfo.profileImage = userDetails.image_url;
          sessionStorage.setItem("sal_userInfo", JSON.stringify(userInfo));
        }else{
          setProfileImage(DEFAULT_IMAGE);
          //remove profileImage from session storage
          const userInfo = JSON.parse(sessionStorage.getItem("sal_userInfo"));
          userInfo.profileImage = null;
          sessionStorage.setItem("sal_userInfo", JSON.stringify(userInfo));
        }
      }
      // Set pagination data from API response
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError(error?.response?.data?.message);
      console.error("Error in sending message:", error);
    }
    dispatch(stopLoader());
  };

  // Validation functions
  const validatePhoneNumber = (value) => {
    // Only allow + and numerical values, max 13 characters, no spaces
    const phoneRegex = /^[+]?[0-9]{0,12}$/;
    if (!phoneRegex.test(value)) {
      return phoneNumberError;
    }
    return null;
  };

  const validateDesignation = (value) => {
    // Only allow alphabets and dots, max 50 characters
    const designationRegex = /^[a-zA-Z\s.]{0,50}$/;
    if (!designationRegex.test(value)) {
      return designationError;
    }
    return null;
  };

  const validateFullName = (value) => {
    // Only allow alphabets and spaces, max 40 characters
    // const nameRegex = /^[a-zA-Z\s]{0,40}$/;
    // if (!nameRegex.test(value)) {
    //   return fullNameError;
    // }
    return null;
  };

  const validateCompanyName = (value) => {
    // Allow alphabets, hyphens, dots, and spaces, max 100 characters
    const companyRegex = /^[a-zA-Z\s.-]{0,100}$/;
    if (!companyRegex.test(value)) {
      return companyNameError;
    }
    return null;
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    let error = null;

    // Apply field-specific validation
    switch (name) {
      case "phone_number":
        error = validatePhoneNumber(value);
        break;
      case "designation":
        error = validateDesignation(value);
        break;
      case "full_name":
        error = validateFullName(value);
        break;
      case "company_name":
        error = validateCompanyName(value);
        break;
      default:
        break;
    }

    // Update user details
    setUserDetails({ ...userDetails, [name]: value });

    // Update errors
    setErrors({ ...errors, [name]: error });
  };

  // Check if form is valid
  const isFormValid = () => {
    return Object.values(errors).every(
      (error) => error === null || error === undefined
    );
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.profileSection}>
        <div className={styles.profileInfo}>
          <img
            src={profileImage}
            alt="Profile"
            className={styles.profileImage}
          />
          <div className={styles.userInfo}>
            <h5>{userDetails.full_name}</h5>
            <p>{userDetails.email}</p>
          </div>
        </div>
        <div className={styles.buttonGroupImage}>
          <button className={styles.updateButton}>
            <label htmlFor="fileInput" className={styles.fileLabel}>
              <ArrowUpFromLine size={14} />
              <span className={styles.fileLabelText}>{changeProfileText}</span>
            </label>
            <input
              id="fileInput"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className={styles.fileInput}
            />
          </button>
          <button className={styles.deleteButton}>
            <Trash onClick={handleDeleteProfileImage} size={16} />
          </button>
        </div>
      </div>
      <form className={styles.detailsForm}>
        <div className={styles.inputGroup}>
          <div className={styles.inputGroupItem}>
            <label>{fullName}</label>
            <input
              className={`${styles.input} ${
                errors.full_name ? styles.inputError : ""
              }`}
              type="text"
              name="full_name"
              value={userDetails.full_name || ""}
              placeholder={fullNamePlaceholder}
              onChange={onChangeHandler}
              disabled={true}
              style={{ cursor: 'not-allowed' , backgroundColor: '#f7fafd' }}
            />
            {errors.full_name && (
              <span className={styles.errorText}>{errors.full_name}</span>
            )}
          </div>
          <div className={styles.inputGroupItem}>
            <label>{phoneNumber}</label>
            <input
              className={`${styles.input} ${
                errors.phone_number ? styles.inputError : ""
              }`}
              type="text"
              name="phone_number"
              placeholder={phoneNumberPlaceholder}
              value={userDetails.phone_number || ""}
              onChange={onChangeHandler}
            />
            {errors.phone_number && (
              <span className={styles.errorText}>{errors.phone_number}</span>
            )}
          </div>
        </div>
        <div className={styles.inputGroup}>
          <div className={styles.inputGroupItem}>
            <label>{designation}</label>
            <input
              className={`${styles.input} ${
                errors.designation ? styles.inputError : ""
              }`}
              type="text"
              name="designation"
              value={userDetails.designation || ""}
              placeholder={designationPlaceholder}
              onChange={onChangeHandler}
            />
            {errors.designation && (
              <span className={styles.errorText}>{errors.designation}</span>
            )}
          </div>
          <div className={styles.inputGroupItem}>
            <label>{companyName}</label>
            <input
              className={`${styles.input} ${
                errors.company_name ? styles.inputError : ""
              }`}
              type="text"
              name="company_name"
              placeholder={companyNamePlaceholder}
              value={userDetails.company_name || ""}
              onChange={onChangeHandler}
            />
            {errors.company_name && (
              <span className={styles.errorText}>{errors.company_name}</span>
            )}
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <CustomButtonLight text={cancelText} width="100%" />
          <CustomButton
            text={saveChangesText}
            width="100%"
            onClick={handleUpdateUserDetails}
            disabled={!isFormValid()}
          />
        </div>
      </form>
    </div>
  );
};

export default DetailsForm;
